import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, Crown, Loader2, XCircle, ArrowRight } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import PublicNavbar from "../components/PublicNavbar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BACKGROUND_IMAGE = "https://customer-assets.emergentagent.com/job_e54d5963-4f37-44ed-8ca3-11e6a6219384/artifacts/4zfnbqje_IMG_3607.png";

const MembershipSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  
  const [status, setStatus] = useState("checking"); // checking, success, failed
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus("failed");
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("failed");
      return;
    }

    try {
      const response = await fetch(`${API}/membership/status/${sessionId}`);
      if (response.ok) {
        const data = await response.json();
        setPaymentData(data);
        
        if (data.payment_status === "paid") {
          setStatus("success");
          return;
        } else if (data.status === "expired") {
          setStatus("failed");
          return;
        }
      }
      
      // Continue polling
      setTimeout(() => {
        setAttempts(prev => prev + 1);
        pollPaymentStatus();
      }, pollInterval);
    } catch {
      setTimeout(() => {
        setAttempts(prev => prev + 1);
        pollPaymentStatus();
      }, pollInterval);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: '#0a0500' }}>
      {/* Background */}
      <div 
        className="fixed inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${BACKGROUND_IMAGE})`, opacity: 0.3 }}
      />
      <div 
        className="fixed inset-0"
        style={{ background: 'radial-gradient(ellipse 80% 70% at 50% 40%, transparent 0%, rgba(0,0,0,0.6) 70%, rgba(0,0,0,0.9) 100%)' }}
      />

      <PublicNavbar />

      <div className="relative z-10 min-h-screen flex items-center justify-center p-6">
        <Card 
          className="w-full max-w-md"
          style={{
            background: 'linear-gradient(145deg, rgba(20, 15, 5, 0.95) 0%, rgba(15, 10, 5, 0.95) 100%)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 215, 0, 0.2)',
            boxShadow: '0 0 50px rgba(255, 215, 0, 0.15)'
          }}
        >
          <CardContent className="pt-8 pb-8 text-center">
            {status === "checking" && (
              <>
                <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ background: 'rgba(255, 215, 0, 0.15)' }}>
                  <Loader2 size={40} className="text-[#FFD700] animate-spin" />
                </div>
                <h2 className="text-2xl font-bold text-[#FFFFFF] mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Processing Payment...
                </h2>
                <p className="text-[#E5C558]">
                  Please wait while we confirm your membership.
                </p>
              </>
            )}

            {status === "success" && (
              <>
                <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ background: 'rgba(34, 197, 94, 0.2)' }}>
                  <CheckCircle size={40} className="text-[#22C55E]" />
                </div>
                <h2 className="text-2xl font-bold text-[#FFFFFF] mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Welcome to DopeInHD!
                </h2>
                <p className="text-[#E5C558] mb-6">
                  Your membership has been activated successfully.
                </p>
                
                {paymentData && (
                  <div className="p-4 rounded-lg mb-6" style={{ background: 'rgba(255, 215, 0, 0.1)', border: '1px solid rgba(255, 215, 0, 0.2)' }}>
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Crown size={20} className="text-[#FFD700]" />
                      <span className="text-[#FFD700] font-semibold">
                        {paymentData.metadata?.plan_name || "Membership"}
                      </span>
                    </div>
                    <p className="text-[#E5C558] text-sm">
                      ${(paymentData.amount || 0).toFixed(2)}/month
                    </p>
                  </div>
                )}
                
                <p className="text-[#71717A] text-sm mb-6">
                  A confirmation email has been sent to your inbox with all the details.
                </p>
                
                <div className="space-y-3">
                  <Button
                    onClick={() => navigate("/staff/login")}
                    className="w-full bg-gradient-to-r from-[#FFD700] to-[#D4AF37] text-[#0a0500] font-semibold"
                  >
                    Go to Login
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate("/")}
                    className="w-full border-[#27272A] text-[#E5C558] hover:text-[#FFD700]"
                  >
                    Back to Home
                  </Button>
                </div>
              </>
            )}

            {status === "failed" && (
              <>
                <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ background: 'rgba(239, 68, 68, 0.2)' }}>
                  <XCircle size={40} className="text-[#EF4444]" />
                </div>
                <h2 className="text-2xl font-bold text-[#FFFFFF] mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Payment Issue
                </h2>
                <p className="text-[#E5C558] mb-6">
                  We couldn't confirm your payment. This might be due to a timeout or cancellation.
                </p>
                
                <div className="space-y-3">
                  <Button
                    onClick={() => navigate("/membership")}
                    className="w-full bg-gradient-to-r from-[#FFD700] to-[#D4AF37] text-[#0a0500] font-semibold"
                  >
                    Try Again
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate("/contact")}
                    className="w-full border-[#27272A] text-[#E5C558] hover:text-[#FFD700]"
                  >
                    Contact Support
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MembershipSuccess;
